import positions from 'positions';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap_white.css';
import React from 'react';
import { findDOMNode } from 'react-dom';
import './bi-tooltip.scss';

// Local stand-in for rc-trigger's AlignType (an undeclared transitive dependency of
// rc-tooltip) — only the field this component actually reads.
interface PopupAlign {
	points?: string[];
}

const placementsMap: Record<string, string> = {
	tc: 'top center',
	bc: 'bottom center',
	cl: 'center right',
	cr: 'center left',
};

interface Props {
	inTable?: boolean;
	overlay?: string | JSX.Element;
	children: React.ReactElement;
	placement?: string;
	noNesting?: boolean;
	backgroundColor?: 'white' | 'black';
}

const uglyCorrectionForCenterAlign = 6;

const backgroundBlackStyle: React.CSSProperties = { backgroundColor: 'black', color: 'white' };
const backgroundWhiteStyle: React.CSSProperties = { backgroundColor: 'white', color: 'black' };

/**
 * Show a (top-positioned) tooltip for an element. Tested on <div>-elements
 * Usage: Use `overlay` to set a message and wrap your elements in this component.
 */
export default class BiTooltip extends React.PureComponent<Props> {
	public handleAlignPopup = (tooltipEl: HTMLElement, align: PopupAlign) => {
		const arrowEl = tooltipEl.querySelector('.rc-tooltip-arrow') as HTMLElement | null;
		if (!arrowEl) {
			return;
		}

		const targetEl = findDOMNode(this) as HTMLElement | null;
		if (!targetEl) {
			return;
		}

		const point0 = align?.points?.[0] || '';
		const point1 = align?.points?.[1] || '';

		const position = positions(arrowEl, placementsMap[point0], targetEl, placementsMap[point1]);

		if (point0 === 'tc' || point0 === 'bc') {
			arrowEl.style.top = '';
			arrowEl.style.left = `${position.left + uglyCorrectionForCenterAlign}px`;
		} else {
			arrowEl.style.top = `${position.top}px`;
			arrowEl.style.left = '';
		}
	};

	public render() {
		const { inTable, overlay, children, placement, noNesting, backgroundColor, ...restProps } = this.props;

		const tooltip = (
			<Tooltip
				overlayInnerStyle={backgroundColor === 'white' ? backgroundWhiteStyle : backgroundBlackStyle}
				placement={placement || 'top'}
				overlayClassName="rc-tooltip-custom"
				overlay={overlay || ''}
				mouseLeaveDelay={0}
				onPopupAlign={this.handleAlignPopup}
				{...restProps}
			>
				{children}
			</Tooltip>
		);

		// NOTE: If the root child has a width larger than its content, the tooltip arrow
		// won't center. `display: inline-block` (wrapped in a `<div>`) fixes that.
		return overlay ? (
			noNesting ? (
				tooltip
			) : (
				<div className={inTable ? 'display-inline-grid' : 'display-inline-block'}>{tooltip}</div>
			)
		) : (
			children
		);
	}
}
